import React, { FC, useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { Button, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONSTANTS } from 'config/constants';
import { TeethRange } from 'models/material';
import { RestorationJaw } from 'models/order';
import { jawOfRange, rangeOfJaw } from 'components/order-flow/shared/restoration-input-helper';
import Tooth from './tooth.component';
import { upperTeethData, lowerTeethData } from './teeth-svg-data';
import TeethLegend from 'components/lab-order/teeth-structure/legend.component';

const { UPPER_JAW, LOWER_JAW } = CONSTANTS;

export enum SelectionMode {
    SINGLE,
    RANGE,
    JAW,
}

const TEETH_BY_JAW: [RestorationJaw, string[]][] = [
    [RestorationJaw.UPPER_JAW, UPPER_JAW],
    [RestorationJaw.LOWER_JAW, LOWER_JAW],
];

/** The jaws a selection touches, in chart order. */
const jawsOfTeeth = (selection: number[]): RestorationJaw[] =>
    TEETH_BY_JAW.filter(([, teeth]) => teeth.some((tooth) => selection.includes(Number(tooth)))).map(([jaw]) => jaw);

const teethOfJaws = (jaws: RestorationJaw[]): number[] =>
    TEETH_BY_JAW.filter(([jaw]) => jaws.includes(jaw)).flatMap(([, teeth]) => teeth.map(Number));

/** The one jaw a stored jaw selection stands for, expanded into the jaws it covers. */
const jawsOfSelection = (jaw: RestorationJaw): RestorationJaw[] =>
    jaw === RestorationJaw.BOTH_JAWS ? [RestorationJaw.UPPER_JAW, RestorationJaw.LOWER_JAW] : [jaw];

const selectionOfJaws = (jaws: RestorationJaw[]): RestorationJaw | undefined =>
    jaws.length > 1 ? RestorationJaw.BOTH_JAWS : jaws[0];

type TeethSelectionProps = {
    selectionMode: SelectionMode;
    onChange?: (selectedTeeth?: number[], teethRange?: TeethRange) => void;
    onTeethStateChange?: (selectedTeethState: Map<number, number>) => void;
    teeth?: number[];
    teethState?: Map<number, number>;
    teethRange?: TeethRange;
    showJawButtons?: boolean;
    isPonticExtracted?: boolean;
};
const TeethSelection: FC<TeethSelectionProps> = ({
    selectionMode,
    onChange,
    onTeethStateChange,
    teeth,
    teethState,
    teethRange,
    showJawButtons,
    isPonticExtracted,
}) => {
    const { t: translate } = useTranslation(['orders']);
    const [selectedTeeth, setSelectedTeeth] = useState<number[]>([]);
    const [selectedTeethRange, setSelectedTeethRange] = useState<{ start: number | null; end: number | null }>({
        start: null,
        end: null,
    });
    const [hoveredTeeth, setHoveredTeeth] = useState<number[]>([]);
    const [selectedTeethState, setSelectedTeethState] = useState<Map<number, number>>(new Map());

    useEffect(() => {
        setSelectedTeeth(teeth || []);
        if (teethRange) {
            // A stored jaw selection comes back as the range the picker holds it as; both jaws
            // span the two of them, which handleSetTeethRange rejects as a range (DD-3765).
            const jaw = jawOfRange(teethRange);
            if (jaw) {
                showJaws(jawsOfSelection(jaw));
            } else {
                handleSetTeethRange(teethRange.start, teethRange.end);
            }
        }
        if (teethState) {
            setSelectedTeethState(teethState || new Map());
        }
    }, [teeth, teethRange, teethState]);

    useEffect(() => {
        const handleGlobalClick = (event: MouseEvent) => {
            if (selectionMode === SelectionMode.RANGE) {
                const target = event.target as HTMLElement;
                if (target.closest('[data-tooth]')) {
                    // Do nothing if a tooth is clicked
                    return;
                }
                setSelectedTeethRange({ start: null, end: null });
                setHoveredTeeth([]);
            }
        };

        document.addEventListener('click', handleGlobalClick);
        return () => {
            document.removeEventListener('click', handleGlobalClick);
        };
    }, []);

    const handleSvgClick = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        const target = event.target as HTMLElement;
        const toothWrapper = target.closest('[data-tooth]');
        if (toothWrapper) {
            const toothNumber = parseInt(toothWrapper.getAttribute('data-tooth') || '');
            if (toothNumber) {
                switch (selectionMode) {
                    case SelectionMode.JAW:
                        if (UPPER_JAW.includes(toothNumber.toString())) {
                            handleSelectJaw(UPPER_JAW, RestorationJaw.UPPER_JAW);
                        } else if (LOWER_JAW.includes(toothNumber.toString())) {
                            handleSelectJaw(LOWER_JAW, RestorationJaw.LOWER_JAW);
                        }
                        break;
                    case SelectionMode.SINGLE:
                        handleSingleClick(toothNumber);
                        break;
                    default:
                        break;
                }
            }
        } else {
            if (selectionMode === SelectionMode.RANGE) {
                setSelectedTeethRange({ start: null, end: null });
                setHoveredTeeth([]);
            }
        }
    };

    const handleSvgMouseDown = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        if (selectionMode !== SelectionMode.RANGE) return;
        const target = event.target as HTMLElement;
        const toothWrapper = target.closest('[data-tooth]');
        if (toothWrapper) {
            const toothNumber = parseInt(toothWrapper.getAttribute('data-tooth') || '');
            if (toothNumber) {
                handleRangeClick(toothNumber);
            }
        } else {
            setSelectedTeethRange({ start: null, end: null });
            setHoveredTeeth([]);
        }
    };

    const handleSingleClick = (toothNumber: number) => {
        if (isPonticExtracted) {
            setSelectedTeethState((prev) => {
                const newMap = new Map(prev);
                const currentState = newMap.get(toothNumber) ?? 0;
                const nextState = currentState < 4 ? currentState + 1 : undefined;
                if (nextState) {
                    newMap.set(toothNumber, nextState);
                } else {
                    newMap.delete(toothNumber);
                }
                if (onTeethStateChange) {
                    onTeethStateChange(newMap);
                }
                return newMap;
            });
        } else {
            const newTeeth = selectedTeeth.includes(toothNumber)
                ? selectedTeeth.filter((tooth) => tooth !== toothNumber)
                : [...selectedTeeth, toothNumber];
            setSelectedTeeth(newTeeth);
            if (onChange) {
                onChange(newTeeth);
            }
        }
    };

    const handleRangeClick = (toothNumber: number) => {
        if (selectedTeethRange.start === null) {
            setSelectedTeethRange({ start: toothNumber, end: null });
            setSelectedTeeth([]);
            setHoveredTeeth([toothNumber]);
        } else {
            const teethRange = handleSetTeethRange(selectedTeethRange.start, toothNumber);
            if (onChange && teethRange) {
                onChange(undefined, teethRange);
            }
        }
    };

    const handleSetTeethRange = (start: number, end: number) => {
        if (start) {
            const isSameJaw =
                (UPPER_JAW.includes(start.toString()) && UPPER_JAW.includes(end.toString())) ||
                (LOWER_JAW.includes(start.toString()) && LOWER_JAW.includes(end.toString()));

            if (isSameJaw) {
                const allTeeth = [...UPPER_JAW, ...LOWER_JAW].map(Number);
                const startIndex = allTeeth.indexOf(start);
                const endIndex = allTeeth.indexOf(end);

                if (startIndex !== endIndex) {
                    const rangeTeeth = allTeeth.slice(
                        Math.min(startIndex, endIndex),
                        Math.max(startIndex, endIndex) + 1,
                    );
                    setSelectedTeeth(rangeTeeth);
                    setSelectedTeethRange({ start: null, end: null });
                    setHoveredTeeth([]);
                    return { start, end };
                } else {
                    setSelectedTeethRange({ start: null, end: null });
                    setHoveredTeeth([]);
                }
            } else {
                setSelectedTeethRange({ start: null, end: null });
                setHoveredTeeth([]);
            }
        }
    };

    const handleSvgMouseMove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        if (selectionMode === SelectionMode.RANGE && selectedTeethRange.start !== null) {
            const target = event.target as HTMLElement;
            const toothWrapper = target.closest('[data-tooth]');
            if (toothWrapper) {
                const toothNumber = parseInt(toothWrapper.getAttribute('data-tooth') || '');
                if (toothNumber) {
                    const isSameJaw =
                        (UPPER_JAW.includes(selectedTeethRange.start.toString()) &&
                            UPPER_JAW.includes(toothNumber.toString())) ||
                        (LOWER_JAW.includes(selectedTeethRange.start.toString()) &&
                            LOWER_JAW.includes(toothNumber.toString()));

                    if (isSameJaw) {
                        const allTeeth = [...UPPER_JAW, ...LOWER_JAW].map(Number);
                        const startIndex = allTeeth.indexOf(selectedTeethRange.start);
                        const endIndex = allTeeth.indexOf(toothNumber);
                        const rangeTeeth = allTeeth.slice(
                            Math.min(startIndex, endIndex),
                            Math.max(startIndex, endIndex) + 1,
                        );
                        setHoveredTeeth(rangeTeeth);
                    } else {
                        // Clear hover effect if jaws are different
                        setHoveredTeeth([]);
                    }
                }
            }
        } else {
            setHoveredTeeth([]);
        }
    };

    const handleSvgMouseLeave = () => {
        setHoveredTeeth([]);
    };

    /** Paint a jaw selection without reporting it, for loading a stored one back in. */
    const showJaws = (jaws: RestorationJaw[]) => {
        setSelectedTeeth(teethOfJaws(jaws));
        setSelectedTeethRange({ start: null, end: null });
        setHoveredTeeth([]);
    };

    /**
     * A jaw-mode selection is one or both whole jaws, handed over as the range the picker holds
     * a jaw as. It is never handed over as a list of teeth: that is how a jaw ends up charged
     * by its 16 teeth on a per-tooth action (DD-3765).
     */
    const selectJaws = (jaws: RestorationJaw[]) => {
        const selection = selectionOfJaws(jaws);
        showJaws(jaws);
        if (onChange) {
            onChange(undefined, selection ? rangeOfJaw(selection) : undefined);
        }
    };

    /** Clicking a jaw that is already selected takes it out of the selection. */
    const toggleJaw = (jaw: RestorationJaw) => {
        const selectedJaws = jawsOfTeeth(selectedTeeth);
        selectJaws(selectedJaws.includes(jaw) ? selectedJaws.filter((entry) => entry !== jaw) : [...selectedJaws, jaw]);
    };

    const isJawPicker = selectionMode === SelectionMode.JAW && !showJawButtons;

    const handleSelectJaw = (jaw: string[], which: RestorationJaw) => {
        const jawTeethNumbers = jaw.map(Number);
        if (showJawButtons) {
            // Select all teeth in the jaw individually, used in lab created orders
            if (isPonticExtracted) {
                const teeth = new Map(selectedTeethState.entries());
                jawTeethNumbers.forEach((tooth) => {
                    teeth.set(tooth, 1);
                });
                setSelectedTeethState(teeth);
                if (onTeethStateChange) {
                    onTeethStateChange(teeth);
                }
            } else {
                const otherTeeth = selectedTeeth.filter((tooth) => !jawTeethNumbers.includes(tooth));
                const newSelectedTeeth = [...otherTeeth, ...jawTeethNumbers];
                setSelectedTeeth(newSelectedTeeth);
                if (onChange) {
                    onChange(newSelectedTeeth);
                }
            }
        } else {
            toggleJaw(which);
        }
    };

    const handleClearJaw = (jaw: string[], which: RestorationJaw) => {
        const jawTeethNumbers = jaw.map(Number);
        if (isPonticExtracted) {
            const teeth = new Map(selectedTeethState.entries());
            jawTeethNumbers.forEach((toothNumber) => {
                teeth.delete(toothNumber);
            });
            setSelectedTeethState(teeth);
            if (onTeethStateChange) {
                onTeethStateChange(teeth);
            }
        } else if (isJawPicker) {
            selectJaws(jawsOfTeeth(selectedTeeth).filter((entry) => entry !== which));
        } else {
            const newSelection = jawTeethNumbers.filter((tooth) => selectedTeeth.includes(tooth));
            const newSelectedTeeth = selectedTeeth.filter((tooth) => !newSelection.includes(tooth));
            setSelectedTeeth(newSelectedTeeth);
            if (onChange) {
                onChange(newSelectedTeeth);
            }
        }
    };

    return (
        <Box display="flex" alignItems="center" data-teeth-selection>
            <Paper variant="outlined" sx={{ padding: 1, width: '100%', userSelect: 'none' }}>
                {/*upper jaw*/}
                <svg
                    onClick={selectionMode !== SelectionMode.RANGE ? handleSvgClick : undefined}
                    onMouseDown={selectionMode === SelectionMode.RANGE ? handleSvgMouseDown : undefined}
                    onMouseMove={selectionMode === SelectionMode.RANGE ? handleSvgMouseMove : undefined}
                    onMouseLeave={selectionMode === SelectionMode.RANGE ? handleSvgMouseLeave : undefined}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1306 100"
                    fill="#eee"
                    style={{ zIndex: 100000 }}
                >
                    <g>
                        {upperTeethData.map((teeth) => (
                            <Tooth
                                key={teeth.number}
                                number={teeth.number}
                                path={teeth.path}
                                textPosition={teeth.textPosition}
                                selectedTeeth={selectedTeeth}
                                hoveredTeeth={hoveredTeeth}
                                selectedTeethState={selectedTeethState}
                                isPonticExtracted={isPonticExtracted}
                            />
                        ))}
                    </g>
                </svg>
                {/*lower jaw*/}
                <svg
                    onClick={selectionMode !== SelectionMode.RANGE ? handleSvgClick : undefined}
                    onMouseDown={selectionMode === SelectionMode.RANGE ? handleSvgMouseDown : undefined}
                    onMouseMove={selectionMode === SelectionMode.RANGE ? handleSvgMouseMove : undefined}
                    onMouseLeave={selectionMode === SelectionMode.RANGE ? handleSvgMouseLeave : undefined}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 130 1306 95"
                    fill="#eee"
                >
                    <g>
                        {lowerTeethData.map((teeth) => (
                            <Tooth
                                key={teeth.number}
                                number={teeth.number}
                                path={teeth.path}
                                textPosition={teeth.textPosition}
                                selectedTeeth={selectedTeeth}
                                hoveredTeeth={hoveredTeeth}
                                selectedTeethState={selectedTeethState}
                                isPonticExtracted={isPonticExtracted}
                            />
                        ))}
                    </g>
                </svg>
                {isPonticExtracted && (
                    <Box mt={2}>
                        <TeethLegend />
                    </Box>
                )}
            </Paper>
            <Box ml={1} display="flex" flexDirection="column" gap={2} width="250px">
                <Box display="flex" gap={1}>
                    <Button
                        onClick={() => handleClearJaw(UPPER_JAW, RestorationJaw.UPPER_JAW)}
                        data-test="teeth-selection.clear-upper-jaw"
                    >
                        {translate('common:CLEAR')}
                    </Button>
                    {showJawButtons ? (
                        <Button
                            onClick={() => handleSelectJaw(UPPER_JAW, RestorationJaw.UPPER_JAW)}
                            data-test="teeth-selection.select-upper-jaw"
                        >
                            {translate('orders:SELECT_UPPER_JAW')}
                        </Button>
                    ) : undefined}
                </Box>
                <Box display="flex" gap={1}>
                    <Button
                        onClick={() => handleClearJaw(LOWER_JAW, RestorationJaw.LOWER_JAW)}
                        data-test="teeth-selection.clear-lower-jaw"
                    >
                        {translate('common:CLEAR')}
                    </Button>
                    {showJawButtons ? (
                        <Button
                            onClick={() => handleSelectJaw(LOWER_JAW, RestorationJaw.LOWER_JAW)}
                            data-test="teeth-selection.select-lower-jaw"
                        >
                            {translate('orders:SELECT_LOWER_JAW')}
                        </Button>
                    ) : undefined}
                </Box>
            </Box>
        </Box>
    );
};

export default TeethSelection;
